// Shared types and interfaces for accounting views
// Extracted from stores/AccountingStore to avoid dependencies

// Enums
export enum InvoiceStatus {
  DRAFT = "draft",
  SENT = "sent",
  PAID = "paid",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
}

export enum BillStatus {
  DRAFT = "draft",
  RECEIVED = "received",
  PAID = "paid",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
}

export enum CustomerStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export enum SupplierStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PENDING = "pending",
  BLOCKED = "blocked",
}

export enum PaymentMethod {
  CASH = "cash",
  CHECK = "check",
  CREDIT_CARD = "credit_card",
  BANK_TRANSFER = "bank_transfer",
  OTHER = "other",
}

export enum PaymentStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export enum AccountType {
  ASSET = "asset",
  LIABILITY = "liability",
  EQUITY = "equity",
  REVENUE = "revenue",
  EXPENSE = "expense",
}

// Interfaces
export interface Invoice {
  id: number;
  invoiceNumber: string;
  customerId: number;
  customerName?: string; // Optional since it's not in the API response but may be populated by UI
  invoiceDate: string; // YYYY-MM-DD format
  invoiceTime: string; // HH:MM:SS format
  dueDate: string; // YYYY-MM-DD format
  dueTime: string; // HH:MM:SS format
  status: InvoiceStatus;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  currency: string;
  exchangeRate: number;
  paymentTerms: number; // days
  notes?: string;
  internalNotes?: string;
  createdDate: string; // YYYY-MM-DD format
  createdTime: string; // HH:MM:SS format
  updatedDate: string; // YYYY-MM-DD format
  updatedTime: string; // HH:MM:SS format
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  lineItems?: InvoiceLineItem[]; // Optional since it's not in the API response but may be populated separately
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  plantId?: string;
  glAccountId?: string;
  taxable?: boolean;
}

export interface Bill {
  id: string;
  billNumber: string;
  supplierId: string;
  supplierName: string;
  vendorId: string;
  referenceNumber: string;
  billDate: string; // YYYY-MM-DD format
  issueDate: string; // YYYY-MM-DD format
  dueDate: string; // YYYY-MM-DD format
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  status: BillStatus;
  notes?: string;
  lineItems: BillLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface BillLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  totalAmount: number;
  total: number;
  plantId?: string;
  glAccountId?: string;
}

export interface Customer {
  id: string;
  customerCode: string;
  companyName: string;
  contactFirstName: string;
  contactLastName: string;
  email: string;
  phone: string;
  mobile?: string;
  website?: string;
  billingAddressLine1: string;
  billingAddressLine2?: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  shippingAddressLine1: string;
  shippingAddressLine2?: string;
  shippingCity: string;
  shippingState: string;
  shippingPostalCode: string;
  shippingCountry: string;
  taxNumber: string;
  paymentTerms: number; // days
  creditLimit: number;
  isActive: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Supplier {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  website?: string;
  taxId?: string;
  paymentTerms: string;
  balance: number;
  status: SupplierStatus;
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  shippingAddress?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contactPerson?: {
    name: string;
    email: string;
    phone: string;
    position: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: string;
  paymentNumber: string;
  paymentType: "customer_payment" | "supplier_payment";
  amount: number;
  paymentDate: string; // YYYY-MM-DD format
  paymentTime: string; // HH:MM:SS format
  paymentMethod: PaymentMethod;
  referenceNumber?: string;
  bankAccount?: string;
  currency: string;
  exchangeRate: number;
  customerId?: number | null;
  supplierId?: number | null;
  invoiceId?: number | null;
  billId?: number | null;
  status: PaymentStatus;
  notes?: string;
  createdDate: string;
  createdTime: string;
  updatedDate: string;
  updatedTime: string;
  created_at: string;
  updated_at: string;
  // Legacy fields for backward compatibility
  type?: "received" | "sent";
  reference?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChartOfAccount {
  id: number;
  accountCode: string;
  accountName: string;
  accountType: AccountType;
  accountSubType?: string;
  parentAccountCode?: string;
  balance: number;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface JournalEntry {
  id: string;
  entryNumber: string;
  entryDate: string; // YYYY-MM-DD format
  description: string;
  reference?: string;
  totalDebit: number;
  totalCredit: number;
  status: "draft" | "posted" | "reversed";
  lineItems: JournalLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface JournalLineItem {
  id: string;
  accountId: string;
  accountName: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
}

export interface AccountingSummary {
  totalReceivables: number;
  totalPayables: number;
  overdueReceivables: number;
  overduePayables: number;
  currentMonthRevenue: number;
  currentMonthExpenses: number;
  netIncome: number;
  cashBalance: number;
}

export interface DashboardData {
  totalReceivables: number;
  outstandingInvoicesCount: number;
  totalPayables: number;
  outstandingBillsCount: number;
  overdueInvoicesAmount: number;
  overdueInvoicesCount: number;
  billsDueSoonAmount: number;
  billsDueSoonCount: number;
  currentMonthRevenue: number;
  currentMonthExpenses: number;
  netIncome: number;
  cashBalance: number;
}

// Job Costing Types
export enum JobStatus {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  CLOSED = "closed",
  CANCELLED = "cancelled",
}

export enum CostCategory {
  MATERIAL = "material",
  LABOR = "labor",
  OVERHEAD = "overhead",
  EQUIPMENT = "equipment",
  SUBCONTRACTOR = "subcontractor",
}

export interface Job {
  id: string;
  jobNumber: string;
  jobName: string;
  customerId?: string;
  customerName?: string;
  productionOrderId?: string;
  status: JobStatus;
  startDate?: string;
  completedDate?: string;
  estimatedCost: number;
  actualCost: number;
  budgetedHours: number;
  actualHours: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CostAllocation {
  id: string;
  jobId: string;
  category: CostCategory;
  description: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  allocationDate: string;
  referenceId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface JobBudget {
  id: string;
  jobId: string;
  category: CostCategory;
  budgetedAmount: number;
  actualAmount: number;
  variance: number;
  variancePercent: number;
}

// Inventory Valuation Types
export enum InventoryValuationMethod {
  FIFO = "fifo",
  LIFO = "lifo",
  WEIGHTED_AVERAGE = "weighted_average",
  SPECIFIC_IDENTIFICATION = "specific_identification",
}

export enum InventoryType {
  RAW_MATERIAL = "raw_material",
  WORK_IN_PROCESS = "work_in_process",
  FINISHED_GOODS = "finished_goods",
}

export interface InventoryItem {
  id: string;
  itemNumber: string;
  itemName: string;
  type: InventoryType;
  valuationMethod: InventoryValuationMethod;
  quantityOnHand: number;
  unitCost: number;
  totalValue: number;
  reorderPoint?: number;
  location?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryTransaction {
  id: string;
  itemId: string;
  transactionDate: string;
  transactionType: "receipt" | "issue" | "adjustment";
  quantity: number;
  unitCost: number;
  totalCost: number;
  referenceId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryValuation {
  id: string;
  itemId: string;
  valuationDate: string;
  method: InventoryValuationMethod;
  quantity: number;
  averageCost: number;
  totalValue: number;
  cogsAmount: number;
}

// WIP (Work in Process) Types
export interface WIPItem {
  id: string;
  jobId: string;
  productionOrderId?: string;
  stage: string;
  materialCost: number;
  laborCost: number;
  overheadCost: number;
  totalCost: number;
  percentComplete: number;
  startDate: string;
  estimatedCompletionDate?: string;
  createdAt: string;
  updatedAt: string;
}

// Cost Allocation Configuration
export interface CostAllocationRule {
  id: string;
  name: string;
  category: CostCategory;
  allocationMethod: "direct" | "percentage" | "hours" | "units";
  isActive: boolean;
  formula?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// PHASE 3: Production Cost Tracking Types
// ============================================================================

export enum ProductionCostCategory {
  DIRECT_MATERIAL = "direct_material",
  DIRECT_LABOR = "direct_labor",
  MANUFACTURING_OVERHEAD = "manufacturing_overhead",
  INDIRECT_MATERIAL = "indirect_material",
  INDIRECT_LABOR = "indirect_labor",
  UTILITIES = "utilities",
  MAINTENANCE = "maintenance",
  QUALITY_CONTROL = "quality_control",
}

export enum CostVarianceType {
  FAVORABLE = "favorable",
  UNFAVORABLE = "unfavorable",
  NEUTRAL = "neutral",
}

export enum LaborType {
  DIRECT = "direct",
  INDIRECT = "indirect",
  OVERTIME = "overtime",
  SHIFT_DIFFERENTIAL = "shift_differential",
}

export enum EquipmentCostType {
  DEPRECIATION = "depreciation",
  MAINTENANCE = "maintenance",
  OPERATION = "operation",
  SETUP = "setup",
}

// Production Cost Tracking
export interface ProductionCost {
  id: string;
  productionOrderId: string;
  jobId?: string;
  category: ProductionCostCategory;
  description: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  standardCost: number;
  variance: number;
  varianceType: CostVarianceType;
  variancePercent: number;
  costDate: string;
  referenceId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Labor Tracking
export interface LaborEntry {
  id: string;
  employeeId: string;
  employeeName?: string;
  productionOrderId?: string;
  jobId?: string;
  laborType: LaborType;
  workDate: string;
  startTime: string;
  endTime: string;
  hoursWorked: number;
  regularHours: number;
  overtimeHours: number;
  hourlyRate: number;
  overtimeRate: number;
  totalLabor: number;
  standardHours?: number;
  varianceHours?: number;
  department?: string;
  workCenter?: string;
  operation?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Equipment Utilization Costing
export interface EquipmentCost {
  id: string;
  equipmentId: string;
  equipmentName?: string;
  productionOrderId?: string;
  jobId?: string;
  costType: EquipmentCostType;
  usageDate: string;
  hoursUsed: number;
  costPerHour: number;
  totalCost: number;
  standardCost?: number;
  variance?: number;
  varianceType?: CostVarianceType;
  maintenanceCategory?: string;
  operatorId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Variance Analysis
export interface CostVariance {
  id: string;
  productionOrderId: string;
  jobId?: string;
  period: string; // YYYY-MM format
  category: ProductionCostCategory;
  standardCost: number;
  actualCost: number;
  variance: number;
  variancePercent: number;
  varianceType: CostVarianceType;
  quantityVariance: number;
  priceVariance: number;
  analysisNotes?: string;
  createdAt: string;
  updatedAt: string;
}

// Standard Costing
export interface StandardCost {
  id: string;
  productId?: string;
  productionLineId?: string;
  category: ProductionCostCategory;
  description: string;
  standardQuantity: number;
  standardRate: number;
  standardCost: number;
  effectiveDate: string;
  expirationDate?: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Production Cost Summary
export interface ProductionCostSummary {
  productionOrderId: string;
  jobId?: string;
  period: string;
  totalDirectMaterial: number;
  totalDirectLabor: number;
  totalManufacturingOverhead: number;
  totalProductionCost: number;
  totalStandardCost: number;
  totalVariance: number;
  variancePercent: number;
  unitCount: number;
  costPerUnit: number;
  standardCostPerUnit: number;
  laborHours: number;
  equipmentHours: number;
  materialVariance: number;
  laborVariance: number;
  overheadVariance: number;
}

// ============================================================================
// PHASE 4: Reporting & Analytics Types
// ============================================================================

export enum ReportPeriod {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  QUARTERLY = "quarterly",
  YEARLY = "yearly",
  CUSTOM = "custom",
}

export enum ReportFormat {
  PDF = "pdf",
  EXCEL = "excel",
  CSV = "csv",
  JSON = "json",
}

// Financial Statements
export interface IncomeStatement {
  id: string;
  period: string;
  startDate: string;
  endDate: string;
  revenue: {
    sales: number;
    otherIncome: number;
    totalRevenue: number;
  };
  costOfGoodsSold: {
    directMaterials: number;
    directLabor: number;
    manufacturingOverhead: number;
    totalCOGS: number;
  };
  grossProfit: number;
  grossProfitMargin: number;
  operatingExpenses: {
    selling: number;
    administrative: number;
    depreciation: number;
    other: number;
    totalOperatingExpenses: number;
  };
  operatingIncome: number;
  operatingMargin: number;
  otherExpenses: number;
  netIncome: number;
  netProfitMargin: number;
}

export interface BalanceSheet {
  id: string;
  asOfDate: string;
  assets: {
    currentAssets: {
      cash: number;
      accountsReceivable: number;
      inventory: number;
      prepaidExpenses: number;
      totalCurrentAssets: number;
    };
    fixedAssets: {
      propertyPlantEquipment: number;
      accumulatedDepreciation: number;
      netFixedAssets: number;
    };
    totalAssets: number;
  };
  liabilities: {
    currentLiabilities: {
      accountsPayable: number;
      accruedExpenses: number;
      currentPortionLongTermDebt: number;
      totalCurrentLiabilities: number;
    };
    longTermLiabilities: {
      longTermDebt: number;
      otherLiabilities: number;
      totalLongTermLiabilities: number;
    };
    totalLiabilities: number;
  };
  equity: {
    commonStock: number;
    retainedEarnings: number;
    totalEquity: number;
  };
  totalLiabilitiesAndEquity: number;
}

export interface CashFlowStatement {
  id: string;
  period: string;
  startDate: string;
  endDate: string;
  operatingActivities: {
    netIncome: number;
    depreciation: number;
    accountsReceivableChange: number;
    inventoryChange: number;
    accountsPayableChange: number;
    netCashFromOperations: number;
  };
  investingActivities: {
    equipmentPurchases: number;
    assetSales: number;
    netCashFromInvesting: number;
  };
  financingActivities: {
    debtProceeds: number;
    debtRepayments: number;
    dividendsPaid: number;
    netCashFromFinancing: number;
  };
  netCashChange: number;
  beginningCash: number;
  endingCash: number;
}

// Production Profitability Reports
export interface JobProfitability {
  jobId: string;
  jobNumber: string;
  jobName: string;
  customerName?: string;
  revenue: number;
  totalCost: number;
  grossProfit: number;
  grossProfitMargin: number;
  estimatedCost: number;
  costVariance: number;
  costVariancePercent: number;
  status: JobStatus;
  completionDate?: string;
}

export interface ProductLineProfitability {
  productLine: string;
  revenue: number;
  costOfGoodsSold: number;
  grossProfit: number;
  grossProfitMargin: number;
  unitsSold: number;
  averagePricePerUnit: number;
  averageCostPerUnit: number;
}

export interface CustomerProfitability {
  customerId: string;
  customerName: string;
  totalRevenue: number;
  totalCost: number;
  grossProfit: number;
  grossProfitMargin: number;
  jobCount: number;
  averageJobValue: number;
  averageJobProfit: number;
}

// Job Cost Variance Analysis
export interface JobCostVariance {
  jobId: string;
  jobNumber: string;
  jobName: string;
  budgetedCost: number;
  actualCost: number;
  variance: number;
  variancePercent: number;
  materialVariance: number;
  laborVariance: number;
  overheadVariance: number;
  equipmentVariance: number;
  status: JobStatus;
  forecastToComplete: number;
  estimatedFinalCost: number;
}

export interface CategoryVariance {
  category: string;
  budgeted: number;
  actual: number;
  variance: number;
  variancePercent: number;
  varianceType: CostVarianceType;
}

// AR/AP Aging Reports
export interface AgingBucket {
  current: number; // 0-30 days
  days31to60: number;
  days61to90: number;
  over90: number;
  total: number;
}

export interface ARAgingReport {
  id: string;
  asOfDate: string;
  customerAging: {
    customerId: string;
    customerName: string;
    aging: AgingBucket;
  }[];
  totalAging: AgingBucket;
}

export interface APAgingReport {
  id: string;
  asOfDate: string;
  supplierAging: {
    supplierId: string;
    supplierName: string;
    aging: AgingBucket;
  }[];
  totalAging: AgingBucket;
}

// Cash Flow Forecast
export interface CashFlowForecast {
  id: string;
  forecastDate: string;
  forecastPeriod: number; // days ahead
  currentCash: number;
  projectedReceipts: {
    date: string;
    amount: number;
    source: string;
  }[];
  projectedPayments: {
    date: string;
    amount: number;
    payee: string;
  }[];
  dailyProjections: {
    date: string;
    receipts: number;
    payments: number;
    netCash: number;
    runningBalance: number;
  }[];
  minimumCash: number;
  minimumCashDate: string;
}

// Executive KPI Dashboard
export interface ExecutiveKPIs {
  period: string;
  startDate: string;
  endDate: string;
  financial: {
    revenue: number;
    revenueGrowth: number;
    grossProfit: number;
    grossProfitMargin: number;
    netIncome: number;
    netProfitMargin: number;
    ebitda: number;
    ebitdaMargin: number;
  };
  operational: {
    jobsCompleted: number;
    jobsInProgress: number;
    averageJobDuration: number;
    onTimeDeliveryRate: number;
    productionEfficiency: number;
  };
  cashFlow: {
    currentCash: number;
    cashGrowth: number;
    daysOfCash: number;
    operatingCashFlow: number;
  };
  receivables: {
    totalAR: number;
    overdueAR: number;
    dso: number; // Days Sales Outstanding
    collectionEfficiency: number;
  };
  payables: {
    totalAP: number;
    overdueAP: number;
    dpo: number; // Days Payables Outstanding
  };
}

// Cost Trend Analysis
export interface CostTrend {
  period: string;
  category: ProductionCostCategory | CostCategory;
  actualCost: number;
  standardCost: number;
  variance: number;
  variancePercent: number;
  trend: "increasing" | "decreasing" | "stable";
}

// Report Configuration
export interface ReportConfig {
  id: string;
  reportType: string;
  reportName: string;
  period: ReportPeriod;
  startDate?: string;
  endDate?: string;
  filters?: Record<string, any>;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  includeCharts?: boolean;
  format?: ReportFormat;
}

// ===================================================================
// PHASE 5: AUTOMATION & INTEGRATION TYPES
// ===================================================================

// Automation Workflow Types
export enum WorkflowType {
  SHIPPING_TO_INVOICE = "shipping_to_invoice",
  MILESTONE_BILLING = "milestone_billing",
  RECURRING_INVOICE = "recurring_invoice",
  BATCH_PAYMENT = "batch_payment",
  MONTH_END_CLOSE = "month_end_close",
  AUTO_JOURNAL_ENTRY = "auto_journal_entry",
  QC_INVOICE_HOLD = "qc_invoice_hold",
}

export enum WorkflowStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PAUSED = "paused",
  ERROR = "error",
}

export enum WorkflowExecutionStatus {
  PENDING = "pending",
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
  SKIPPED = "skipped",
}

export enum RecurrenceFrequency {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  QUARTERLY = "quarterly",
  YEARLY = "yearly",
}

export enum QCStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  PASSED = "passed",
  FAILED = "failed",
  CONDITIONAL_PASS = "conditional_pass",
}

// Automation Workflow Definition
export interface AutomationWorkflow {
  id: string;
  workflowType: WorkflowType;
  name: string;
  description: string;
  status: WorkflowStatus;
  triggerConfig: WorkflowTrigger;
  actionConfig: WorkflowAction;
  conditions?: WorkflowCondition[];
  isActive: boolean;
  lastExecutedAt?: string;
  nextExecutionAt?: string;
  executionCount: number;
  successCount: number;
  failureCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// Workflow Trigger Configuration
export interface WorkflowTrigger {
  triggerType: "event" | "schedule" | "manual";
  eventType?: string; // e.g., "shipment.completed", "qc.completed"
  scheduleConfig?: {
    frequency: RecurrenceFrequency;
    dayOfWeek?: number; // 0-6 (Sunday-Saturday)
    dayOfMonth?: number; // 1-31
    time?: string; // HH:MM format
    timezone?: string;
  };
  filters?: Record<string, any>;
}

// Workflow Action Configuration
export interface WorkflowAction {
  actionType: string; // e.g., "create_invoice", "create_payment", "post_journal"
  parameters: Record<string, any>;
  retryConfig?: {
    maxRetries: number;
    retryDelay: number; // milliseconds
  };
  notificationConfig?: {
    onSuccess?: string[]; // User IDs to notify
    onFailure?: string[]; // User IDs to notify
    emailTemplate?: string;
  };
}

// Workflow Condition
export interface WorkflowCondition {
  field: string;
  operator: "equals" | "not_equals" | "greater_than" | "less_than" | "contains" | "exists";
  value: any;
  logicalOperator?: "and" | "or";
}

// Workflow Execution Log
export interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName?: string;
  status: WorkflowExecutionStatus;
  triggeredBy: "system" | "user" | "schedule" | "event";
  triggeredByUserId?: string;
  startedAt: string;
  completedAt?: string;
  duration?: number; // milliseconds
  inputData?: Record<string, any>;
  outputData?: Record<string, any>;
  errorMessage?: string;
  errorStack?: string;
  retryCount: number;
  createdAt: string;
}

// Shipping to Invoice Automation
export interface ShippingInvoiceRule {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  customerId?: string;
  productId?: string;
  invoiceTemplateId?: string;
  paymentTerms: number; // days
  autoSend: boolean;
  requireQCApproval: boolean;
  billingType: "full" | "partial" | "milestone";
  partialBillingPercent?: number;
  createdAt: string;
  updatedAt: string;
}

// QC Integration Types
export interface QCInspection {
  id: string;
  inspectionNumber: string;
  shipmentId?: string;
  productionOrderId?: string;
  jobId?: string;
  productId: string;
  productName?: string;
  quantity: number;
  inspectionDate: string;
  inspectorId: string;
  inspectorName?: string;
  status: QCStatus;
  overallResult: "pass" | "fail" | "conditional";
  defectCount: number;
  criticalDefects: number;
  majorDefects: number;
  minorDefects: number;
  certificateIssued: boolean;
  certificateNumber?: string;
  certificateUrl?: string;
  notes?: string;
  holdInvoicing: boolean;
  releaseDate?: string;
  releasedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface QCInspectionResult {
  id: string;
  inspectionId: string;
  criteriaId: string;
  criteriaName: string;
  criteriaType: "visual" | "dimensional" | "functional" | "material";
  specification: string;
  actualValue?: string;
  result: "pass" | "fail" | "na";
  severity: "critical" | "major" | "minor";
  notes?: string;
  imageUrls?: string[];
  createdAt: string;
}

export interface QCCertificate {
  id: string;
  certificateNumber: string;
  inspectionId: string;
  jobId?: string;
  shipmentId?: string;
  invoiceId?: string;
  issueDate: string;
  expiryDate?: string;
  certificateType: "material" | "conformance" | "inspection" | "test";
  documentUrl: string;
  signedBy: string;
  signedByName?: string;
  customerId: string;
  customerName?: string;
  productSummary: string;
  warrantyPeriod?: number; // days
  warrantyExpiryDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Batch Processing Types
export interface BatchProcess {
  id: string;
  batchType: "payment" | "invoice" | "journal_entry" | "month_end_close";
  name: string;
  description: string;
  status: WorkflowExecutionStatus;
  totalItems: number;
  processedItems: number;
  successfulItems: number;
  failedItems: number;
  progress: number; // 0-100
  startedBy: string;
  startedByName?: string;
  startedAt: string;
  completedAt?: string;
  estimatedCompletion?: string;
  errorSummary?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface BatchProcessItem {
  id: string;
  batchProcessId: string;
  itemType: string;
  itemId: string;
  itemReference?: string;
  status: WorkflowExecutionStatus;
  errorMessage?: string;
  processedAt?: string;
  retryCount: number;
  metadata?: Record<string, any>;
  createdAt: string;
}

// Recurring Invoice Configuration
export interface RecurringInvoice {
  id: string;
  customerId: string;
  customerName?: string;
  templateName: string;
  description: string;
  frequency: RecurrenceFrequency;
  startDate: string;
  endDate?: string;
  nextInvoiceDate: string;
  lastInvoiceDate?: string;
  invoiceCount: number;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  lineItems: RecurringInvoiceLineItem[];
  paymentTerms: number;
  autoSend: boolean;
  isActive: boolean;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RecurringInvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  accountId: string;
  accountName?: string;
  taxable: boolean;
}

// Month-End Close Process
export interface MonthEndClose {
  id: string;
  periodMonth: string; // YYYY-MM format
  status: "not_started" | "in_progress" | "completed" | "failed";
  startedBy?: string;
  startedByName?: string;
  startedAt?: string;
  completedAt?: string;
  steps: MonthEndCloseStep[];
  totalSteps: number;
  completedSteps: number;
  errorSteps: number;
  notes?: string;
  isClosed: boolean;
  closedBy?: string;
  closedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MonthEndCloseStep {
  id: string;
  monthEndCloseId: string;
  stepNumber: number;
  stepName: string;
  stepType: string;
  status: WorkflowExecutionStatus;
  isRequired: boolean;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  performedBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Auto-Posting Rules
export interface AutoPostingRule {
  id: string;
  ruleName: string;
  description: string;
  isActive: boolean;
  triggerEvent: string; // e.g., "invoice.paid", "bill.received"
  sourceEntityType: string; // e.g., "invoice", "bill", "payment"
  journalEntryTemplate: {
    description: string;
    lines: AutoPostingRuleLine[];
  };
  conditions?: WorkflowCondition[];
  priority: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AutoPostingRuleLine {
  accountId: string;
  accountType: "debit" | "credit";
  amountSource: string; // Field path in source entity, e.g., "totalAmount", "taxAmount"
  percentage?: number; // If only a percentage should be posted
  description?: string;
}

// ===================================================================
// PHASE 6: ADVANCED FEATURES TYPES
// ===================================================================

// Multi-Entity Types
export enum EntityType {
  PARENT = "parent",
  SUBSIDIARY = "subsidiary",
  DIVISION = "division",
  BRANCH = "branch",
}

export enum EntityStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PENDING_SETUP = "pending_setup",
  ARCHIVED = "archived",
}

export enum IntercompanyTransactionType {
  SALE = "sale",
  PURCHASE = "purchase",
  LOAN = "loan",
  EXPENSE_ALLOCATION = "expense_allocation",
  MANAGEMENT_FEE = "management_fee",
  DIVIDEND = "dividend",
  CAPITAL_CONTRIBUTION = "capital_contribution",
}

export enum IntercompanyTransactionStatus {
  PENDING = "pending",
  POSTED = "posted",
  RECONCILED = "reconciled",
  ELIMINATED = "eliminated",
}

export interface LegalEntity {
  id: string;
  entityCode: string;
  entityName: string;
  legalName: string;
  entityType: EntityType;
  parentEntityId?: string;
  parentEntityName?: string;
  taxId: string;
  registrationNumber?: string;
  incorporationDate: string;
  fiscalYearEnd: string; // MM-DD format
  baseCurrency: string;
  status: EntityStatus;
  address: EntityAddress;
  contact: EntityContact;
  banking: EntityBanking[];
  settings: EntitySettings;
  chartOfAccountsId?: string;
  useParentCOA: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EntityAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface EntityContact {
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  secondaryContactName?: string;
  secondaryContactEmail?: string;
  secondaryContactPhone?: string;
}

export interface EntityBanking {
  id: string;
  bankName: string;
  accountNumber: string;
  accountType: "checking" | "savings" | "money_market";
  routingNumber?: string;
  swiftCode?: string;
  iban?: string;
  isPrimary: boolean;
  accountId?: string; // Link to Chart of Accounts
}

export interface EntitySettings {
  defaultPaymentTerms: number;
  defaultTaxRate: number;
  allowIntercompanyTransactions: boolean;
  requireApprovalForIntercompany: boolean;
  autoEliminateIntercompany: boolean;
  consolidationMethod: "equity" | "full";
}

export interface IntercompanyTransaction {
  id: string;
  transactionNumber: string;
  transactionType: IntercompanyTransactionType;
  status: IntercompanyTransactionStatus;
  fromEntityId: string;
  fromEntityName?: string;
  toEntityId: string;
  toEntityName?: string;
  transactionDate: string;
  amount: number;
  currency: string;
  description: string;
  referenceNumber?: string;
  fromJournalEntryId?: string;
  toJournalEntryId?: string;
  eliminationJournalEntryId?: string;
  approvedBy?: string;
  approvedAt?: string;
  reconciledBy?: string;
  reconciledAt?: string;
  eliminatedBy?: string;
  eliminatedAt?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConsolidatedFinancials {
  id: string;
  periodMonth: string; // YYYY-MM
  consolidationType: "full" | "partial";
  parentEntityId: string;
  includedEntities: string[];
  incomeStatement: ConsolidatedIncomeStatement;
  balanceSheet: ConsolidatedBalanceSheet;
  eliminations: ConsolidationElimination[];
  generatedAt: string;
  generatedBy: string;
}

export interface ConsolidatedIncomeStatement {
  revenue: number;
  costOfGoodsSold: number;
  grossProfit: number;
  operatingExpenses: number;
  operatingIncome: number;
  interestExpense: number;
  otherIncome: number;
  netIncome: number;
  eliminations: number;
}

export interface ConsolidatedBalanceSheet {
  totalAssets: number;
  currentAssets: number;
  fixedAssets: number;
  totalLiabilities: number;
  currentLiabilities: number;
  longTermLiabilities: number;
  totalEquity: number;
  eliminations: number;
}

export interface ConsolidationElimination {
  id: string;
  eliminationType: string;
  description: string;
  amount: number;
  fromEntityId: string;
  toEntityId: string;
  journalEntryId?: string;
}

// Audit Trail Types
export enum AuditEventType {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  VIEW = "view",
  APPROVE = "approve",
  REJECT = "reject",
  POST = "post",
  REVERSE = "reverse",
  EXPORT = "export",
  IMPORT = "import",
}

export enum AuditSeverity {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

export interface AuditLog {
  id: string;
  eventType: AuditEventType;
  severity: AuditSeverity;
  entityType: string; // e.g., "invoice", "journal_entry", "payment"
  entityId: string;
  entityName?: string;
  userId: string;
  userName?: string;
  timestamp: string;
  action: string;
  description: string;
  ipAddress?: string;
  userAgent?: string;
  changes?: AuditChange[];
  metadata?: Record<string, any>;
  legalEntityId?: string;
}

export interface AuditChange {
  field: string;
  fieldLabel: string;
  oldValue: any;
  newValue: any;
  changeType: "added" | "modified" | "removed";
}

export interface AuditReport {
  id: string;
  reportName: string;
  reportType: "user_activity" | "entity_changes" | "financial_transactions" | "compliance";
  startDate: string;
  endDate: string;
  filters: AuditReportFilters;
  totalEvents: number;
  criticalEvents: number;
  highSeverityEvents: number;
  affectedUsers: string[];
  affectedEntities: string[];
  generatedAt: string;
  generatedBy: string;
}

export interface AuditReportFilters {
  userId?: string;
  entityType?: string;
  eventType?: AuditEventType;
  severity?: AuditSeverity;
  legalEntityId?: string;
  searchTerm?: string;
}

export interface UserActivitySummary {
  userId: string;
  userName: string;
  totalActions: number;
  createActions: number;
  updateActions: number;
  deleteActions: number;
  viewActions: number;
  lastActivityAt: string;
  criticalActions: number;
}

export interface DataMigrationLog {
  id: string;
  migrationName: string;
  migrationType: "import" | "export" | "transformation";
  sourceSystem: string;
  targetSystem: string;
  startedAt: string;
  completedAt?: string;
  status: "pending" | "in_progress" | "completed" | "failed";
  totalRecords: number;
  processedRecords: number;
  successfulRecords: number;
  failedRecords: number;
  errorSummary?: string[];
  migrationFile?: string;
  executedBy: string;
  notes?: string;
  createdAt: string;
}

// Compliance Types
export enum ComplianceFramework {
  GAAP = "gaap",
  IFRS = "ifrs",
  SOX = "sox",
  GDPR = "gdpr",
  CUSTOM = "custom",
}

export enum ComplianceStatus {
  COMPLIANT = "compliant",
  NON_COMPLIANT = "non_compliant",
  PENDING_REVIEW = "pending_review",
  REMEDIATION_IN_PROGRESS = "remediation_in_progress",
}

export enum DocumentRetentionStatus {
  ACTIVE = "active",
  ARCHIVED = "archived",
  PENDING_DESTRUCTION = "pending_destruction",
  DESTROYED = "destroyed",
}

export interface ComplianceReport {
  id: string;
  reportName: string;
  framework: ComplianceFramework;
  reportingPeriod: string; // YYYY-MM or YYYY-QQ or YYYY
  status: ComplianceStatus;
  dueDate: string;
  submittedDate?: string;
  approvedDate?: string;
  sections: ComplianceReportSection[];
  attachments: ComplianceAttachment[];
  preparedBy: string;
  reviewedBy?: string;
  approvedBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ComplianceReportSection {
  id: string;
  sectionNumber: string;
  sectionName: string;
  requirement: string;
  status: ComplianceStatus;
  evidence?: string;
  findings?: string;
  remediation?: string;
  completedAt?: string;
  completedBy?: string;
}

export interface ComplianceAttachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
  uploadedBy: string;
  documentUrl: string;
  description?: string;
}

export interface ComplianceRule {
  id: string;
  ruleName: string;
  framework: ComplianceFramework;
  ruleCode: string;
  description: string;
  requirement: string;
  category: string;
  severity: "high" | "medium" | "low";
  isActive: boolean;
  validationLogic?: string;
  remediationSteps?: string;
  responsibleRole?: string;
  checkFrequency: "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
  lastCheckedAt?: string;
  nextCheckDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ComplianceViolation {
  id: string;
  ruleId: string;
  ruleName?: string;
  framework: ComplianceFramework;
  violationType: string;
  severity: "critical" | "high" | "medium" | "low";
  description: string;
  detectedAt: string;
  detectedBy: string;
  affectedEntities: string[];
  status: "open" | "acknowledged" | "remediated" | "accepted";
  assignedTo?: string;
  dueDate?: string;
  remediationPlan?: string;
  remediationDate?: string;
  remediatedBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentRetentionPolicy {
  id: string;
  policyName: string;
  documentType: string;
  retentionPeriod: number; // in years
  retentionTrigger: "creation_date" | "completion_date" | "fiscal_year_end";
  legalRequirement?: string;
  framework?: ComplianceFramework;
  destructionMethod: "secure_delete" | "archive" | "physical_destruction";
  approvalRequired: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RetainedDocument {
  id: string;
  documentType: string;
  documentName: string;
  documentReference: string;
  policyId: string;
  policyName?: string;
  entityType: string;
  entityId: string;
  creationDate: string;
  retentionEndDate: string;
  status: DocumentRetentionStatus;
  storageLocation: string;
  documentUrl?: string;
  fileSize?: number;
  archivedAt?: string;
  archivedBy?: string;
  destructionScheduledDate?: string;
  destructionApprovedBy?: string;
  destroyedAt?: string;
  destroyedBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ComplianceCertification {
  id: string;
  certificationType: string;
  certificationBody: string;
  certificateNumber: string;
  issueDate: string;
  expiryDate: string;
  status: "active" | "expired" | "pending_renewal" | "revoked";
  scope: string;
  legalEntityId?: string;
  certificateUrl?: string;
  renewalRequired: boolean;
  renewalDate?: string;
  renewalNotificationDays: number;
  lastAuditDate?: string;
  nextAuditDate?: string;
  auditorName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== Asset Types ====================

export interface Asset {
  id: string;
  companyId: string;
  internalAssetNumber: string;
  cpaAssetNumber?: string;
  description: string;
  acquisitionDate: string;
  acquisitionCost: number;
  currentValue?: number;
  depreciationMethod?: "straight_line" | "declining_balance" | "units_of_production";
  usefulLifeYears?: number;
  salvageValue?: number;
  glAccountCode: string;
  locationId?: string;
  status: "active" | "disposed" | "fully_depreciated";
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== Plant/Location Types ====================

export interface PlantLocation {
  id: string;
  name: string;
  isDefault: boolean;
  isActive?: boolean;
}

export const PLANTS_BY_COMPANY: Record<string, PlantLocation[]> = {
  AX: [
    { id: "100", name: "Riverside", isDefault: true },
    { id: "200", name: "Lakewood", isDefault: false },
    { id: "300", name: "Oakdale", isDefault: false },
    { id: "400", name: "Pinehurst", isDefault: false },
  ],
  Summit: [{ id: "100", name: "Highland", isDefault: true }],
  SierraValley: [{ id: "100", name: "Greenfield", isDefault: true }],
  NP: [{ id: "100", name: "Main", isDefault: true }],
  EG: [{ id: "100", name: "Main", isDefault: true }],
  Harbor: [{ id: "100", name: "Main", isDefault: true }],
};

export const getPlantsForCompany = (companyId: string): PlantLocation[] => {
  return PLANTS_BY_COMPANY[companyId] || [];
};

export const getDefaultPlantForCompany = (
  companyId: string
): PlantLocation | undefined => {
  const plants = getPlantsForCompany(companyId);
  return plants.find((p) => p.isDefault) || plants[0];
};

// ==================== Dimensional Accounting Types ====================

export interface GLDimension {
  dimensionId: string;
  name: string;
  code: string;
  type: DimensionType;
  isRequired: boolean;
  isActive: boolean;
}

export type DimensionType =
  | "plant"
  | "job"
  | "phase"
  | "cost_type"
  | "department"
  | "equipment"
  | "employee";

// ==================== GL Transaction Types ====================

export interface GLTransaction {
  transactionId: string;
  journalEntryId?: string;
  journalLineItemId?: string;
  sourceType: TransactionSourceType;
  sourceId: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  debitAmount: number;
  creditAmount: number;
  signedAmount?: number | null;
  runningBalance?: number;
  plantId?: string;
  jobId?: string;
  phaseCode?: string;
  costType?: string;
  departmentId?: string;
  equipmentId?: string;
  employeeId?: string;
  vendorId?: string;
  customerId?: string;
  journalReferencePrefix?: string;
  isReconciled?: boolean;
  depositNumber?: string;
  checkNumber?: string;
  checkDate?: string;
  vendorName?: string;
  customerName?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  poNumber?: string;
  discountAmount?: number;
  transactionDate: string;
  postingDate: string;
  periodId: string;
  description: string;
  reference?: string;
  createdAt: string;
  createdBy: string;
  isReversed: boolean;
  reversalId?: string;
}

export type { glTransaction as GLTransactionChild } from "types/api-types/financial/gl-transactions/gl-transactions.types";

export type TransactionSourceType =
  | "journal_entry"
  | "invoice"
  | "bill"
  | "payment"
  | "payroll"
  | "inventory"
  | "wip_adjustment"
  | "depreciation"
  | "recurring"
  | "import";

// ==================== Account Balance Types ====================

export interface AccountBalance {
  balanceId: string;
  accountId: string;
  periodId: string;
  plantId?: string;
  beginningBalance: number;
  periodDebits: number;
  periodCredits: number;
  endingBalance: number;
  budgetAmount?: number;
  varianceAmount?: number;
  variancePercent?: number;
}

// ==================== Fiscal Period Types ====================

export interface FiscalPeriod {
  periodId: string;
  periodNumber: number;
  periodName: string;
  fiscalYear: number;
  startDate: string;
  endDate: string;
  isGLLocked: boolean;
  isAPLocked: boolean;
  isARLocked: boolean;
  isPayrollLocked: boolean;
  lockedAt?: string;
  lockedBy?: string;
  isYearEnd: boolean;
  isAdjustmentPeriod: boolean;
}

export type LockableModule = "gl" | "ap" | "ar" | "payroll";

// ==================== Enhanced Chart of Accounts Types ====================

export interface EnhancedGLAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: AccountType;
  parentAccountId?: string;
  normalBalance: "debit" | "credit";
  isActive: boolean;
  isHeader: boolean;
  isBankAccount: boolean;
  isCreditCard: boolean;
  bankAccountNumber?: string;
  balance: number;
  description?: string;
  costCategory?: PrecastCostCategory;
  defaultPlantId?: string;
  allowedPlantIds?: string[];
  requiresJob: boolean;
  requiresPhase: boolean;
  requiresCostType: boolean;
  createdAt: string;
  updatedAt: string;
}

export type PrecastCostCategory =
  | "direct_materials"
  | "direct_labor"
  | "manufacturing_overhead"
  | "selling_expense"
  | "administrative_expense"
  | "other_income_expense";

// ==================== Precast Job Cost Types ====================

export interface JobCostAllocation {
  allocationId: string;
  jobId: string;
  jobNumber: string;
  jobName: string;
  phaseCode: string;
  phaseDescription: string;
  costType: PrecastCostType;
  glAccountId: string;
  budgetAmount: number;
  committedAmount: number;
  actualAmount: number;
  remainingAmount: number;
}

export type PrecastCostType =
  | "raw_materials"
  | "forms"
  | "embeds"
  | "labor_production"
  | "labor_finishing"
  | "equipment"
  | "freight_inbound"
  | "freight_outbound"
  | "subcontract"
  | "overhead"
  | "other";

// ==================== WIP Schedule Types ====================

export interface WIPScheduleEntry {
  wipId: string;
  jobId: string;
  periodId: string;
  originalContractAmount: number;
  approvedChanges: number;
  revisedContractAmount: number;
  totalCostToDate: number;
  estimatedCostToComplete: number;
  estimatedTotalCost: number;
  totalBilledToDate: number;
  retentionHeld: number;
  percentComplete: number;
  earnedRevenue: number;
  overUnderBilling: number;
  wipAdjustmentAmount: number;
  wipGlAccountId: string;
}

// ==================== Bank Reconciliation Types ====================

export interface BankReconciliation {
  reconciliationId: string;
  accountId: string;
  periodId: string;
  status: BankReconciliationStatus;
  statementDate: string;
  statementBalance: number;
  glBeginningBalance: number;
  glEndingBalance: number;
  clearedDebits: number;
  clearedCredits: number;
  outstandingDebits: number;
  outstandingCredits: number;
  difference: number;
  reconciledBy?: string;
  reconciledAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export type BankReconciliationStatus =
  | "not_started"
  | "in_progress"
  | "pending_review"
  | "approved"
  | "locked";

export interface ReconciliationMatch {
  matchId: string;
  reconciliationId: string;
  matchType: "auto" | "manual" | "rule_based";
  glTransactionIds: string[];
  glTotal: number;
  statementLineIds: string[];
  statementTotal: number;
  confidence: number;
  matchReason: string;
  isApproved: boolean;
  approvedBy?: string;
  approvedAt?: string;
}

export interface BankStatementLine {
  lineId: string;
  reconciliationId: string;
  statementDate: string;
  description: string;
  reference?: string;
  amount: number;
  isDebit: boolean;
  isMatched: boolean;
  matchId?: string;
  importedAt: string;
}

// ==================== GL Audit Trail Types ====================

export interface AuditLogEntry {
  auditId: string;
  entityType: string;
  entityId: string;
  action: "create" | "update" | "delete" | "post" | "reverse";
  changedFields: FieldChange[];
  userId: string;
  userName: string;
  timestamp: string;
  sessionId: string;
  ipAddress?: string;
  journalEntryId?: string;
  periodId?: string;
}

export interface FieldChange {
  fieldName: string;
  oldValue: unknown;
  newValue: unknown;
  displayOldValue: string;
  displayNewValue: string;
}

// ==================== Command Palette Types ====================

export interface CommandPaletteItem {
  id: string;
  label: string;
  shortcut?: string;
  category: CommandCategory;
  action: () => void;
  icon?: string;
  keywords?: string[];
}

export type CommandCategory =
  | "navigation"
  | "create"
  | "reports"
  | "search"
  | "settings";

// ==================== Report Configuration Types ====================

export interface ReportConfiguration {
  reportId: string;
  reportType: FinancialReportType;
  name: string;
  plantConfig: {
    enabled: boolean;
    mode: "all" | "selected" | "each";
    showPlantColumn: boolean;
    subtotalByPlant: boolean;
    splitByPlant: boolean;
  };
  dateConfig: {
    type: "period" | "date_range" | "as_of";
    allowComparison: boolean;
  };
  columns: ReportColumn[];
  drillDown: {
    enabled: boolean;
    levels: DrillDownLevel[];
  };
  exportFormats: ("pdf" | "excel" | "csv")[];
}

export type FinancialReportType =
  | "trial_balance"
  | "income_statement"
  | "balance_sheet"
  | "general_ledger"
  | "account_activity"
  | "wip_schedule"
  | "job_cost_summary";

export interface ReportColumn {
  key: string;
  label: string;
  width?: number;
  align?: "left" | "center" | "right";
  format?: "text" | "currency" | "percent" | "date" | "number";
  isSummable?: boolean;
  isVisible?: boolean;
}

export interface DrillDownLevel {
  name: string;
  groupBy: string;
  showDetail: boolean;
  nextLevel?: string;
}

// ==================== GL View State Types ====================

export type GLViewMode = "summary" | "transactions" | "detail";

export type DateRangeType =
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "last_year"
  | "custom"
  | "ytd";

export type GLSearchField = "description" | "reference" | "account" | "job";
